import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Admin | Globe Vista";
    Promise.all([
      axios.get(`${API}/leads`),
      axios.get(`${API}/newsletter`),
      axios.get(`${API}/stats`),
    ])
      .then(([l, n, s]) => {
        setLeads(l.data);
        setSubs(n.data);
        setStats(s.data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 pt-32 pb-20">
        <h1 className="text-4xl font-heading font-light tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Leads, subscribers and quick stats.</p>

        {loading ? (
          <div className="mt-10 text-muted-foreground">Loading...</div>
        ) : (
          <>
            {stats && (
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Leads" value={stats.leads_total} />
                <StatCard icon={Mail} label="Newsletter Subs" value={stats.subscribers_total} />
                <StatCard icon={Users} label="Students Guided" value={stats.students_guided} />
                <StatCard icon={Users} label="Satisfaction" value={`${stats.satisfaction}%`} />
              </div>
            )}

            <section className="mt-12">
              <h2 className="text-2xl font-heading font-semibold mb-4">Recent Leads ({leads.length})</h2>
              <div className="rounded-2xl border border-border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary text-left">
                      <tr>
                        <Th>Name</Th><Th>Phone</Th><Th>Email</Th><Th>Destination</Th><Th>Course</Th><Th>Qual.</Th><Th>Date</Th>
                      </tr>
                    </thead>
                    <tbody data-testid="leads-table-body">
                      {leads.length === 0 && (
                        <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No leads yet.</td></tr>
                      )}
                      {leads.map((l) => (
                        <tr key={l.id} className="border-t border-border hover:bg-secondary/40">
                          <Td>{l.full_name}</Td><Td>{l.phone}</Td><Td>{l.email}</Td>
                          <Td>{l.destination}</Td><Td>{l.course}</Td><Td>{l.qualification}</Td>
                          <Td>{new Date(l.created_at).toLocaleDateString()}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="text-2xl font-heading font-semibold mb-4">Newsletter Subscribers ({subs.length})</h2>
              <div className="rounded-2xl border border-border overflow-hidden bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left"><tr><Th>Email</Th><Th>Date</Th></tr></thead>
                  <tbody>
                    {subs.length === 0 && <tr><td colSpan="2" className="p-6 text-center text-muted-foreground">No subscribers yet.</td></tr>}
                    {subs.map((s) => (
                      <tr key={s.id} className="border-t border-border">
                        <Td>{s.email}</Td>
                        <Td>{new Date(s.created_at).toLocaleDateString()}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-semibold text-xs tracking-wider uppercase text-muted-foreground">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card">
      <div className="w-10 h-10 rounded-xl bg-[#0F3D8C]/10 dark:bg-[#2563EB]/20 flex items-center justify-center text-[#0F3D8C] dark:text-[#60A5FA]">
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-3xl font-heading font-light">{value}</div>
      <div className="text-xs text-muted-foreground tracking-wide uppercase">{label}</div>
    </div>
  );
}
